import { DOCUMENT } from '@angular/common';
import {
    forwardRef,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    Inject,
    Input,
    NgZone,
    OnDestroy,
    OnInit,
    Output,
    ViewChild,
    HostListener,
    HostBinding,
} from '@angular/core';
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR } from '@angular/forms';

import { keyboard, BaseComponent, ListService } from '@ridango/core';

import { takeUntil } from 'rxjs/operators';

let identifier = 0;

@Component({
    selector: 'ridango-ui-autocomplete-input',
    templateUrl: './autocomplete-input.component.html',
    styleUrls: ['./autocomplete-input.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => AutocompleteInputComponent),
            multi: true,
        },
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AutocompleteInputComponent extends BaseComponent implements OnInit, OnDestroy, ControlValueAccessor {
    @Input() label = 'Input Label';
    @Input() options = [];
    @Input() fullOption = false;
    @Input() noMargin = false;
    @Input() icon = '';
    @Input() iconContrast = '';
    @Input() hasError = '';
    @Input() errorMessage = '';
    @Input() hasOptionIcon = false;
    @Input() allowCustomValue = false;

    selectedIndex = 0;
    showOptions = false;
    selectedOption: any;
    input: FormControl;
    autocompleteId = `autocomplete-input-${++identifier}`;
    optionListId = `autocomplete-options-${identifier}`;
    optionIdPrefix = `${this.autocompleteId}-option-`;
    disabled = false;
    hasFocus = false;
    touched = false;

    @Output() onOptionSelected = new EventEmitter<any>();
    @Output() onInputChanged = new EventEmitter<string>();

    @ViewChild('inputEl') inputRef: ElementRef;
    @ViewChild('items') itemsRef: ElementRef;

    /**
     * Defines a function that tells the component how to display the option inside the input. By default, it displays
     * the option. Use this if the options are objects to tell the component which object property to show
     * in the input when the option is clicked.
     * @param option
     */
    @Input() inputDisplayFn = (option: any) => option;

    /**
     * Defines a function that tells the component how to display the options. Defaults to input display function.
     * Use this if the options are objects and the option display is different from the input display.
     * @param option
     */
    @Input() optionDisplayFn = (option: any) => this.inputDisplayFn(option);

    /**
     * Defines a function that tells the component what icon to display for the options. 'hasOptionIcon' must be set
     * for the component to call this function and display icons.
     */
    @Input() optionIconDisplayFn = (option: any) => null;

    /**
     * Defines an *ngFor trackBy function to be used for the list.
     * @param index
     * @param option
     */
    @Input() trackByFn = (index: number, option: any) => null;

    onChange: any = () => {};
    onTouched: any = () => {};

    setInputValue = (option: any) => this.handleOptionClick(option);

    constructor(
        private listService: ListService,
        private zone: NgZone,
        private cdr: ChangeDetectorRef,
        private eRef: ElementRef,
        @Inject(DOCUMENT) private document: Document,
    ) {
        super();
    }

    @HostBinding('attr.tabindex')
    get tabindex() {
        return (this.hasFocus) ? -1 : 0;
    }

    @HostListener('focus')
    onFocus() {
        this.hasFocus = true;
        this.inputRef.nativeElement.focus();
    }

    onClickOut = (event) => {
        if (!this.eRef.nativeElement.contains(event.target)) {
            this.showOptions = false;
            this.cdr.detectChanges();
        }
    }

    ngOnInit() {
        this.zone.runOutsideAngular(() => this.document.addEventListener('click', this.onClickOut));

        this.input = new FormControl({ value: '', disabled: this.disabled });
        this.input.valueChanges.pipe(takeUntil(this.componentDestroyed$)).subscribe((value: string) => {
            if (!this.showOptions) {
                this.showOptions = true;
            }

            this.onInputChanged.emit(value);
            this.onChange(null);
            this.resetDropDown();

            if (this.allowCustomValue) {
                this.onChange(value);
            }
        });
    }

    ngOnDestroy() {
        this.zone.runOutsideAngular(() => this.document.removeEventListener('click', this.onClickOut));
        super.ngOnDestroy();
    }

    writeValue(option: any): void {
        if (option) {
            this.input.setValue(this.inputDisplayFn(option), { emitEvent: false });
        }
        this.selectedOption = option;
    }

    registerOnChange(fn: any): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: any): void {
        this.onTouched = fn;
    }

    setDisabledState?(isDisabled: boolean): void {
        this.disabled = isDisabled;
    }

    handleOptionClick(option: object | string): void {
        this.input.setValue(this.inputDisplayFn(option), { emitEvent: false });
        this.selectedOption = option;
        this.onChange(option);
        this.onOptionSelected.emit(option);
        this.hideOptions();
    }

    private hideOptions(): void {
        this.showOptions = false;
    }

    private resetDropDown(): void {
        this.itemsRef.nativeElement.scrollTop = 0;
        this.selectedIndex = 0;
    }

    markAsTouched() {
        this.onTouched();
    }

    handleKeydown(event: KeyboardEvent) {
        switch (event.code) {
            case keyboard.ENTER:
                event.preventDefault();
                const option = this.options[this.selectedIndex];
                this.handleOptionClick(option);
                break;
            case keyboard.ESCAPE:
            case keyboard.TAB:
                this.hideOptions();
                break;
            case keyboard.ARROW_DOWN:
                event.preventDefault();
                this.selectNextItem(1);
                break;
            case keyboard.ARROW_UP:
                event.preventDefault();
                this.selectNextItem(-1);
                break;
        }
    }

    private selectNextItem(direction: number): void {
        const size = this.options ? this.options.length : 0;
        this.selectedIndex = this.listService.getNextCyclicIndex(this.selectedIndex, direction, size);
        this.itemsRef.nativeElement.scrollTop = this.itemsRef.nativeElement.children[this.selectedIndex].offsetTop;
    }
}
